// The entitlement-aware order gate, buyer side (build F of
// docs/plans/standard-purchase-flow-2026-10-02.md, section 7).
// Server: worker/src/order-gate.ts. SOP: sop-ee-order-gate.
//
// Before an order page mounts its Spiffy form it asks the engine what this
// buyer is entitled to. The answer can carry:
//   - offers[]          — lines to show ("Member Pro pricing applied", "Use
//                          your free course?", a promotion to accept)
//   - checkout_params   — merged into the Spiffy URL (the member's code, when
//                          one applies — never shown on the page)
//   - gate_id           — written into THIS page's address as ?ee_gate=, so
//                          Spiffy records it as the order's referrer and the
//                          webhook can tie the order back to what was applied
//
// It never blocks a sale: no email, a slow engine (>3s) or an error all mean
// "ready, no offers", and the form mounts at the normal price.

import { useCallback, useEffect, useState } from 'react';

const API = import.meta.env.VITE_API_BASE_URL as string;
const TIMEOUT_MS = 3000;

export interface GateOffer {
  rule_key: string;
  kind: 'benefit' | 'credit' | 'promotion';
  mode: 'auto' | 'ask';
  label: string;
  discount_pct: number | null;
  applied: boolean;
  /** A free course the buyer is owed but cannot use yet (ISO instant). */
  unlocks_at?: string;
}

export type ClaimOutcome =
  | { ok: true; status: 'granted' | 'pending_manual'; program_label: string }
  | { ok: false; error: string };

export interface OrderGateState {
  ready: boolean;
  gateId: string | null;
  offers: GateOffer[];
  checkoutParams: Record<string, string>;
  claim: (ruleKey: string) => Promise<ClaimOutcome>;
  /** Reload the page with an "ask" discount accepted. */
  accept: (ruleKey: string) => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function useOrderGate(input: {
  email: string;
  offerType: 'program' | 'intent';
  offerRef: string;
  plan?: string | null;
  enabled?: boolean;
}): OrderGateState {
  const { email, offerType, offerRef, plan } = input;
  const enabled = input.enabled !== false && EMAIL_RE.test(email) && !!offerRef;
  const [state, setState] = useState<{ ready: boolean; gateId: string | null; offers: GateOffer[]; checkoutParams: Record<string, string> }>(
    { ready: !enabled, gateId: null, offers: [], checkoutParams: {} },
  );

  useEffect(() => {
    if (!enabled) {
      setState({ ready: true, gateId: null, offers: [], checkoutParams: {} });
      return;
    }
    let cancelled = false;
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), TIMEOUT_MS);
    const accept = new URLSearchParams(window.location.search).get('apply');
    (async () => {
      try {
        const res = await fetch(`${API}/api/order-gate/evaluate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, offer_type: offerType, offer_ref: offerRef, plan: plan ?? null, accept }),
          signal: ctl.signal,
        });
        if (!res.ok) throw new Error(`gate ${res.status}`);
        const data = (await res.json()) as { gate_id: string | null; offers: GateOffer[]; checkout_params: Record<string, string> };
        if (cancelled) return;
        if (data.gate_id) {
          // Spiffy stores the embedding page's address as the order referrer.
          const u = new URL(window.location.href);
          u.searchParams.set('ee_gate', data.gate_id);
          u.searchParams.delete('apply');
          window.history.replaceState(window.history.state, '', u.toString());
        }
        setState({ ready: true, gateId: data.gate_id, offers: data.offers ?? [], checkoutParams: data.checkout_params ?? {} });
      } catch (err) {
        if (cancelled) return;
        console.warn('[order-gate] evaluate failed; continuing at the normal price', err);
        setState({ ready: true, gateId: null, offers: [], checkoutParams: {} });
      } finally {
        clearTimeout(timer);
      }
    })();
    return () => { cancelled = true; clearTimeout(timer); ctl.abort(); };
  }, [enabled, email, offerType, offerRef, plan]);

  const claim = useCallback(async (ruleKey: string): Promise<ClaimOutcome> => {
    if (!state.gateId) return { ok: false, error: 'Please refresh the page and try again.' };
    try {
      const res = await fetch(`${API}/api/order-gate/claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ gate_id: state.gateId, rule_key: ruleKey }),
      });
      const data = (await res.json().catch(() => ({}))) as { ok?: boolean; status?: 'granted' | 'pending_manual'; program_label?: string; error?: string };
      if (!res.ok || !data.ok || !data.status) return { ok: false, error: data.error ?? 'Something went wrong. Please try again.' };
      return { ok: true, status: data.status, program_label: data.program_label ?? '' };
    } catch {
      return { ok: false, error: 'We could not reach our system. Please try again in a moment.' };
    }
  }, [state.gateId]);

  const accept = useCallback((ruleKey: string) => {
    const u = new URL(window.location.href);
    u.searchParams.set('apply', ruleKey);
    u.searchParams.delete('ee_gate');
    window.location.replace(u.toString());
  }, []);

  return { ...state, claim, accept };
}

export { withGateParams } from './spiffyUrl';
